import { cn } from '@utils/cn';
import React from 'react';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'shimmer' | 'pulse';
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

export const Skeleton = ({
  variant = 'shimmer',
  width,
  height,
  rounded = 'md',
  className,
  style,
  ...props
}: SkeletonProps) => {
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  };

  const variantClasses = {
    default: 'bg-frozenGrayShades2 dark:bg-frozenGrayShades8',
    shimmer: 'bg-frozenGrayShades2 dark:bg-frozenGrayShades8 relative overflow-hidden',
    pulse: 'bg-frozenGrayShades2 dark:bg-frozenGrayShades8 animate-pulse-subtle',
  };

  const animationClasses = {
    default: '',
    shimmer: 'before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent',
    pulse: '',
  };

  const inlineStyle = {
    ...style,
    ...(width && { width: typeof width === 'number' ? `${width}px` : width }),
    ...(height && { height: typeof height === 'number' ? `${height}px` : height }),
  };

  return (
    <div
      className={cn(
        'block',
        variantClasses[variant],
        roundedClasses[rounded],
        animationClasses[variant],
        className,
      )}
      style={inlineStyle}
      {...props}
      aria-hidden="true"
    />
  );
};

export const SkeletonText = ({
  lines = 1,
  spacing = 'sm',
  className,
  ...props
}: {
  lines?: number;
  spacing?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
} & Omit<SkeletonProps, 'height'>) => {
  const spacingClasses = {
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-3',
    lg: 'space-y-4',
  };

  return (
    <div className={cn(spacingClasses[spacing], className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          height={16}
          width={index === lines - 1 && lines > 1 ? '75%' : '100%'}
          {...props}
        />
      ))}
    </div>
  );
};

export const SkeletonCard = ({
  showAvatar = false,
  showTitle = true,
  showContent = true,
  contentLines = 3,
  className,
  ...props
}: {
  showAvatar?: boolean;
  showTitle?: boolean;
  showContent?: boolean;
  contentLines?: number;
  className?: string;
} & Omit<SkeletonProps, 'width' | 'height'>) => {
  return (
    <div className={cn('space-y-4', className)}>
      {showAvatar && (
        <div className="flex items-center space-x-3">
          <Skeleton width={40} height={40} rounded="full" {...props} />
          <div className="flex-1 space-y-2">
            <Skeleton height={16} width="40%" {...props} />
            <Skeleton height={14} width="60%" {...props} />
          </div>
        </div>
      )}
      
      {showTitle && !showAvatar && (
        <div className="space-y-2">
          <Skeleton height={24} width="60%" {...props} />
          <Skeleton height={16} width="40%" {...props} />
        </div>
      )}
      
      {showContent && (
        <SkeletonText lines={contentLines} spacing="sm" {...props} />
      )}
    </div>
  );
};

export const SkeletonButton = ({
  size = 'md',
  className,
  ...props
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
} & Omit<SkeletonProps, 'width' | 'height'>) => {
  const sizeConfig = {
    sm: { width: 80, height: 32 },
    md: { width: 100, height: 40 },
    lg: { width: 120, height: 48 },
  };

  return (
    <Skeleton
      width={sizeConfig[size].width}
      height={sizeConfig[size].height}
      rounded="md"
      className={className}
      {...props}
    />
  );
};

export const SkeletonAvatar = ({
  size = 'md',
  className,
  ...props
}: {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
} & Omit<SkeletonProps, 'width' | 'height' | 'rounded'>) => {
  const sizeConfig = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 56,
    xl: 72,
  };

  return (
    <Skeleton
      width={sizeConfig[size]}
      height={sizeConfig[size]}
      rounded="full"
      className={className}
      {...props}
    />
  );
};

export const SkeletonTable = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  className,
  ...props
}: {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
} & Omit<SkeletonProps, 'width' | 'height'>) => {
  return (
    <div className={cn('space-y-3', className)}>
      {showHeader && (
        <div className="flex space-x-4">
          {Array.from({ length: columns }).map((_, index) => (
            <Skeleton
              key={`header-${index}`}
              height={20}
              width={`${Math.floor(100 / columns)}%`}
              {...props}
            />
          ))}
        </div>
      )}
      
      <div className="space-y-2">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex space-x-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={`cell-${rowIndex}-${colIndex}`}
                height={16}
                width={`${Math.floor(100 / columns)}%`}
                {...props}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};